/**
 * Security utility functions for the application
 */

import { supabase } from '@/integrations/supabase/client';

export interface SecurityCheck {
  name: string;
  status: 'pass' | 'warning' | 'fail';
  message: string;
  recommendation?: string;
}

/**
 * Performs client-side security checks
 * WARNING: Never rely on localStorage for authorization decisions.
 * Always verify session server-side via Supabase RLS policies.
 */
export const performSecurityChecks = async (): Promise<SecurityCheck[]> => {
  const checks: SecurityCheck[] = [];

  // Check authentication via Supabase session (not localStorage)
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const isAuthenticated = !!session?.user;
    
    checks.push({
      name: 'Authentication Status',
      status: isAuthenticated ? 'pass' : 'warning',
      message: isAuthenticated 
        ? 'User is properly authenticated via Supabase session'
        : 'No active authentication session',
    });
  } catch (error) {
    checks.push({
      name: 'Authentication Status',
      status: 'fail',
      message: 'Failed to verify authentication session',
      recommendation: 'Check Supabase connection'
    });
  }

  // Check for HTTPS in production
  const isHttps = window.location.protocol === 'https:';
  const isLocalhost = window.location.hostname === 'localhost';
  
  if (!isHttps && !isLocalhost) {
    checks.push({
      name: 'HTTPS Connection',
      status: 'warning',
      message: 'Application is not using HTTPS',
      recommendation: 'Enable HTTPS for secure data transmission'
    });
  } else {
    checks.push({
      name: 'HTTPS Connection',
      status: 'pass',
      message: isLocalhost ? 'Running on localhost (development)' : 'Using secure HTTPS connection',
    });
  }

  // Check for Content Security Policy
  const hasCsp = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
  checks.push({
    name: 'Content Security Policy',
    status: hasCsp ? 'pass' : 'warning',
    message: hasCsp ? 'CSP header detected' : 'No CSP header detected',
    recommendation: !hasCsp ? 'Consider implementing Content Security Policy headers' : undefined
  });

  return checks;
};

/**
 * Validates input to prevent XSS attacks
 */
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  
  // Basic XSS prevention - escape HTML characters
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .replace(/`/g, '&#96;')
    .replace(/=/g, '&#61;');
};

/**
 * Comprehensive input validation for different content types
 */
export const validateAndSanitizeInput = (
  input: string, 
  type: 'text' | 'email' | 'url' | 'filename' | 'projectName' | 'password' = 'text',
  maxLength: number = 1000
): { isValid: boolean; sanitizedValue: string; error?: string } => {
  if (!input || typeof input !== 'string') {
    return { isValid: false, sanitizedValue: '', error: 'Input is required' };
  }

  if (input.length > maxLength) {
    return { isValid: false, sanitizedValue: '', error: `Input too long (max ${maxLength} characters)` };
  }

  // Enhanced password validation
  if (type === 'password') {
    if (input.length < 8) {
      return { isValid: false, sanitizedValue: '', error: 'Password must be at least 8 characters long' };
    }
    
    const hasUpperCase = /[A-Z]/.test(input);
    const hasLowerCase = /[a-z]/.test(input);
    const hasNumbers = /\d/.test(input);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(input);
    
    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      return { 
        isValid: false, 
        sanitizedValue: '', 
        error: 'Password must contain uppercase, lowercase, numbers, and special characters' 
      };
    }
  }

  // Check for suspicious patterns
  const suspiciousPatterns = [
    /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /data:text\/html/gi,
    /vbscript:/gi
  ];

  if (suspiciousPatterns.some(pattern => pattern.test(input))) {
    return { isValid: false, sanitizedValue: '', error: 'Input contains potentially dangerous content' };
  }

  let sanitized = sanitizeInput(input.trim());

  // Type-specific validation
  switch (type) {
    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(input)) {
        return { isValid: false, sanitizedValue: '', error: 'Invalid email format' };
      }
      break;
    
    case 'url':
      try {
        const url = new URL(input);
        if (!['http:', 'https:'].includes(url.protocol)) {
          return { isValid: false, sanitizedValue: '', error: 'Only HTTP/HTTPS URLs allowed' };
        }
      } catch {
        return { isValid: false, sanitizedValue: '', error: 'Invalid URL format' };
      }
      break;
    
    case 'filename':
      const invalidChars = /[<>:"/\\|?*\x00-\x1f]/g;
      if (invalidChars.test(input)) {
        return { isValid: false, sanitizedValue: '', error: 'Filename contains invalid characters' };
      }
      break;
    
    case 'projectName':
      // Allow alphanumeric, spaces, hyphens, underscores
      const validNameRegex = /^[a-zA-Z0-9\s\-_]+$/;
      if (!validNameRegex.test(input)) {
        return { isValid: false, sanitizedValue: '', error: 'Project name can only contain letters, numbers, spaces, hyphens, and underscores' };
      }
      break;
  }

  return { isValid: true, sanitizedValue: sanitized };
};

/**
 * Validates file upload security
 */
export const validateFileUpload = (file: File): { valid: boolean; error?: string } => {
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'application/pdf',
    'text/plain',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  if (file.size > maxSize) {
    return { valid: false, error: 'File size exceeds 10MB limit' };
  }

  if (!allowedTypes.includes(file.type)) {
    return { valid: false, error: 'File type not allowed' };
  }

  // Check for suspicious file names
  const suspiciousPatterns = ['.php', '.js', '.html', '.exe', '.bat', '.cmd'];
  const fileName = file.name.toLowerCase();
  
  for (const pattern of suspiciousPatterns) {
    if (fileName.includes(pattern)) {
      return { valid: false, error: 'Potentially unsafe file type detected' };
    }
  }

  return { valid: true };
};

/**
 * Rate limiting utility for form submissions
 */
class RateLimiter {
  private attempts: Map<string, number[]> = new Map();
  private readonly maxAttempts: number;
  private readonly windowMs: number;

  constructor(maxAttempts = 5, windowMs = 60000) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
  }

  canProceed(identifier: string): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(identifier) || [];
    
    // Remove old attempts outside the window
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    
    if (recentAttempts.length >= this.maxAttempts) {
      return false;
    }

    // Add current attempt
    recentAttempts.push(now);
    this.attempts.set(identifier, recentAttempts);
    
    return true;
  }

  getRemainingAttempts(identifier: string): number {
    const now = Date.now();
    const attempts = this.attempts.get(identifier) || [];
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    return Math.max(0, this.maxAttempts - recentAttempts.length);
  }
}

export const createRateLimiter = (maxAttempts?: number, windowMs?: number) => {
  return new RateLimiter(maxAttempts, windowMs);
};

/**
 * Audit logging for security events
 */
export const logSecurityEvent = (event: string, details?: Record<string, any>) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    event,
    details: details || {},
    userAgent: navigator.userAgent,
    url: window.location.href
  };

  // In a production environment, you would send this to a logging service
  console.log('[Security Event]', logEntry);
  
  // Store locally for debugging (consider removing in production)
  try {
    const existingLogs = JSON.parse(localStorage.getItem('security_logs') || '[]');
    existingLogs.push(logEntry);
    
    // Keep only last 50 entries
    if (existingLogs.length > 50) {
      existingLogs.splice(0, existingLogs.length - 50);
    }
    
    localStorage.setItem('security_logs', JSON.stringify(existingLogs));
  } catch (error) {
    console.error('Failed to store security log:', error);
  }
};

/**
 * Enhanced API key validation and security
 */
export const validateApiKey = (key: string, provider: string): { valid: boolean; error?: string } => {
  if (!key || typeof key !== 'string') {
    return { valid: false, error: 'API key is required' };
  }

  const trimmedKey = key.trim();
  
  // Basic length validation based on provider
  const minLengths: Record<string, number> = {
    'openai': 20,
    'stripe': 20,
    'default': 10
  };
  
  const minLength = minLengths[provider.toLowerCase()] || minLengths.default;
  
  if (trimmedKey.length < minLength) {
    return { valid: false, error: `API key appears to be too short for ${provider}` };
  }

  // Check for suspicious patterns
  if (trimmedKey.includes(' ') || /[<>"]/.test(trimmedKey)) {
    return { valid: false, error: 'API key contains invalid characters' };
  }

  // Check if it looks like a placeholder
  const placeholders = ['your_api_key', 'placeholder', 'example', 'test_key'];
  if (placeholders.some(placeholder => trimmedKey.toLowerCase().includes(placeholder))) {
    return { valid: false, error: 'Please provide a real API key, not a placeholder' };
  }

  return { valid: true };
};

/**
 * Secure API key storage with warning
 */
export const storeApiKeySecurely = (key: string, provider: string): { success: boolean; warning?: string } => {
  const validation = validateApiKey(key, provider);
  
  if (!validation.valid) {
    return { success: false, warning: validation.error };
  }

  // Store with encryption warning
  const storageKey = `${provider}_api_key`;
  
  try {
    localStorage.setItem(storageKey, key);
    
    // Log security event
    logSecurityEvent('api_key_stored', { 
      provider,
      keyLength: key.length,
      timestamp: new Date().toISOString()
    });
    
    return { 
      success: true, 
      warning: `⚠️ API key stored in browser localStorage. For production, consider using environment variables or secure key management.`
    };
  } catch (error) {
    return { success: false, warning: 'Failed to store API key securely' };
  }
};
